import {
  Activity,
  AtSign,
  Bell,
  ChevronDown,
  Home,
  Search,
  Server,
  Settings,
  Sparkles,
  Store,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { WorkspaceTabs } from '@/components/layout/WorkspaceTabs';
import { Avatar, Input, Kbd, cn } from '@/components/ui';
import { workspaceTabs } from './fixtures';

/*
  Статическая реплика оболочки V3 для витрины: rail (уровень 1) → навигация модуля (уровень 2) →
  холст, верхний край которого — полоса открытых объектов (уровень 3) с поиском ⌘K справа.
  Собрана только из токенов и примитивов; настоящие PrimaryRail/SecondaryPanel требуют роутер
  и авторизацию, поэтому здесь — макет.
*/

const railItems: { icon: LucideIcon; label: string; active?: boolean; count?: number }[] = [
  { icon: Home, label: 'Главная' },
  { icon: AtSign, label: 'Упоминания', count: 3 },
  { icon: Server, label: 'Хостинг' },
  { icon: Activity, label: 'Инфраструктура', active: true },
  { icon: Store, label: 'Маркетплейс' },
  { icon: Settings, label: 'Настройки' },
];

const navGroups: { title?: string; items: { label: string; count?: number; active?: boolean }[] }[] = [
  {
    items: [
      { label: 'Обзор' },
      { label: 'Проблемы', count: 12, active: true },
      { label: 'Узлы', count: 71 },
      { label: 'Виртуальные машины' },
      { label: 'Группы' },
    ],
  },
  {
    title: 'Сохранённые виды',
    items: [
      { label: 'Ночные алерты' },
      { label: 'DC-2 · критичные' },
      { label: 'Без подтверждения', count: 7 },
    ],
  },
];

/* Группа навигации модуля: заголовок-overline с шевроном (сворачивается), тонкий разделитель сверху,
   те же горизонтальные отступы, что у пунктов, — ритм один на всю панель. */
function NavGroup({ title, children }: { title?: string; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={cn('flex flex-col gap-0.5', title && 'mt-3 border-t border-line pt-3')}>
      {title && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex h-7 w-full items-center justify-between rounded-sm px-2 text-2xs font-semibold tracking-wider text-fg-subtle uppercase transition-colors duration-fast hover:text-fg"
        >
          {title}
          <ChevronDown
            className={cn('size-3.5 transition-transform duration-normal ease-snappy', !open && '-rotate-90')}
          />
        </button>
      )}
      {open && children}
    </div>
  );
}

export function ShowcaseShell({ children, consoleMode }: { children: ReactNode; consoleMode: boolean }) {
  const [active, setActive] = useState('host-338');
  const [tabs, setTabs] = useState(workspaceTabs);

  return (
    <div className="flex h-full min-h-0 gap-3 bg-frame p-3">
      {/* Уровень 1 — rail, всегда тёмный */}
      <nav
        className="flex w-20 shrink-0 flex-col items-center rounded-lg bg-rail py-3 shadow-md"
        aria-label="Разделы"
      >
        <div className="mb-4 grid size-10 place-items-center rounded-md bg-gradient-to-br from-brand to-brand-strong text-base font-extrabold text-on-brand shadow-brand">
          K
        </div>
        <ul className="flex flex-1 flex-col items-center gap-1">
          {railItems.map((it) => (
            <li key={it.label} className="relative">
              <button
                type="button"
                aria-label={it.label}
                className={cn(
                  'grid size-12 place-items-center rounded-md transition-colors duration-fast',
                  it.active
                    ? 'bg-rail-active text-rail-fg'
                    : 'text-rail-fg-muted hover:bg-rail-hover hover:text-rail-fg',
                )}
              >
                <it.icon className="size-5" />
              </button>
              {it.count && (
                <span className="absolute -top-0.5 right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand px-1 text-2xs font-semibold text-on-brand">
                  {it.count}
                </span>
              )}
              {it.active && (
                <span
                  className="absolute top-1/2 -left-3 h-6 w-1 -translate-y-1/2 rounded-full bg-brand"
                  aria-hidden
                />
              )}
            </li>
          ))}
        </ul>
        <button
          type="button"
          aria-label="AI Spotlight"
          className="mb-2 grid size-12 place-items-center rounded-md text-rail-fg-muted hover:bg-rail-hover hover:text-rail-fg"
        >
          <Sparkles className="size-5 text-ai-fg" />
        </button>
        <button
          type="button"
          aria-label="Уведомления"
          className="relative mb-2 grid size-12 place-items-center rounded-md text-rail-fg-muted hover:bg-rail-hover hover:text-rail-fg"
        >
          <Bell className="size-5" />
          <span
            className="absolute top-3 right-3 size-2 rounded-full bg-brand ring-2 ring-rail"
            aria-hidden
          />
        </button>
        <Avatar name="Анна Смирнова" size="md" />
      </nav>

      {/* Климат модуля: режим пульта охватывает навигацию модуля и холст, rail остаётся общим */}
      <div data-mode={consoleMode ? 'console' : undefined} className="flex min-w-0 flex-1 gap-3">
        {/* Уровень 2 — навигация модуля */}
        <aside
          className="flex w-64 shrink-0 flex-col rounded-lg border border-line bg-canvas shadow-inner-card"
          aria-label="Инфраструктура"
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-sm font-semibold text-fg">Инфраструктура</h2>
            <span className="inline-flex items-center gap-1.5 text-2xs text-fg-subtle">
              <span className="size-1.5 animate-ai-pulse rounded-full bg-success" aria-hidden />
              Метрики · 12 с
            </span>
          </div>
          <div className="px-3 pb-2">
            <Input placeholder="Найти узел или VM" leadingIcon={<Search />} aria-label="Поиск" />
          </div>
          <nav className="flex flex-col px-2" aria-label="Навигация модуля">
            {navGroups.map((g, i) => (
              <NavGroup key={g.title ?? i} title={g.title}>
                {g.items.map((it) => (
                  <button
                    key={it.label}
                    type="button"
                    className={cn(
                      'flex h-8 w-full items-center justify-between rounded-sm px-2 text-sm transition-colors duration-fast',
                      it.active
                        ? 'bg-hover font-medium text-fg'
                        : 'text-fg-muted hover:bg-hover hover:text-fg',
                    )}
                  >
                    <span>{it.label}</span>
                    {typeof it.count === 'number' && (
                      <span className="text-2xs text-fg-subtle tabular-nums">{it.count}</span>
                    )}
                  </button>
                ))}
              </NavGroup>
            ))}
          </nav>
          <div className="mt-auto border-t border-line px-4 py-3 text-2xs text-fg-subtle">
            71 узел · 12 локаций · 9 842 VPS
          </div>
        </aside>

        {/* Холст: полоса открытых объектов — его верхний край */}
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-canvas text-fg shadow-inner-card">
          <WorkspaceTabs
            tabs={tabs}
            activeId={active}
            onSelect={setActive}
            onClose={(id) => setTabs((t) => t.filter((x) => x.id !== id))}
            onNew={() => undefined}
            trailing={
              <button
                type="button"
                className="inline-flex h-8 items-center gap-2 rounded-full border border-line bg-card px-3 text-xs text-fg-subtle transition-colors duration-fast hover:border-line-strong hover:text-fg"
              >
                <Search className="size-3.5" />
                Поиск и команды
                <Kbd>⌘K</Kbd>
              </button>
            }
          />
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
